<script lang="ts">
  import CircularProgress from './CircularProgress.svelte'
  import Icon from './Icon.svelte'
  import type {
    HTMLAnchorAttributes,
    HTMLButtonAttributes,
  } from 'svelte/elements'
  import { hasContext, getContext } from 'svelte'
  import { contextKey } from '../../contexts/form'
  import type { FormContext } from '../../types/form'

  type ButtonVariants =
    | 'default'
    | 'flat'
    | 'outlined'
    | 'text'
    | 'tonal'
    | 'link'
  type ButtonTypes = 'submit' | 'button' | 'reset'
  type ButtonColor = 'primary' | 'error' | 'success'
  type IconButtonHoverVariants = 'default' | 'fill'

  interface $$Props extends HTMLButtonAttributes {
    type?: ButtonTypes
    formaction?: string
    appendIcon?: string
    prependIcon?: string
    icon?: boolean | string
    loading?: boolean
    block?: boolean
    href?: string
    rel?: HTMLAnchorAttributes['rel']
    target?: HTMLAnchorAttributes['target']
    rounded?: boolean
    variant?: ButtonVariants
    color?: ButtonColor
    disabled?: HTMLButtonAttributes['disabled']
    iconButtonHoverVariant?: IconButtonHoverVariants
  }

  export let type: ButtonTypes | undefined = undefined
  export let formaction: string | undefined = undefined
  export let appendIcon: string | undefined = undefined
  export let prependIcon: string | undefined = undefined
  export let icon: boolean | string | undefined = undefined
  export let loading = false
  export let block = false
  export let href: string | undefined = undefined
  export let rel: HTMLAnchorAttributes['rel'] | undefined = undefined
  export let target: HTMLAnchorAttributes['target'] | undefined = undefined
  export let rounded = false
  export let variant: ButtonVariants | undefined = undefined
  export let color: ButtonColor | undefined = undefined
  export let disabled: HTMLButtonAttributes['disabled'] | undefined = undefined
  export let iconButtonHoverVariant: IconButtonHoverVariants | undefined =
    undefined

  const { submitting } = hasContext(contextKey)
    ? (getContext(contextKey) as FormContext)
    : { submitting: undefined }

  let isThin = false

  function handleEnter() {
    if (iconButtonHoverVariant === 'fill' && typeof icon === 'string') {
      if (icon.includes('-thin')) {
        isThin = true
      }

      icon = `${icon.replaceAll('-thin', '')}-fill`
    }
  }

  function handleLeave() {
    if (iconButtonHoverVariant === 'fill' && typeof icon === 'string') {
      icon = isThin
        ? `${icon.replaceAll('-fill', '')}-thin`
        : icon.replaceAll('-fill', '')
    }
  }
</script>

{#if href}
  <a
    class:icon={typeof icon === 'boolean'}
    class:icon-button={typeof icon === 'string'}
    class:loading={loading || $submitting}
    class:appendIcon
    class:prependIcon
    class:block
    class={variant}
    class:icon-hover-fill={iconButtonHoverVariant === 'fill'}
    class:rounded
    class:error={color === 'error'}
    class:success={color === 'success'}
    {href}
    {target}
    {rel}
    class:disabled
    on:mouseenter={handleEnter}
    on:focus={handleEnter}
    on:mouseleave={handleLeave}
    on:blur={handleLeave}
    on:click
    on:touchstart
  >
    {#if loading || ($submitting && (type === 'submit' || formaction))}
      <CircularProgress
        indeterminate
        size={icon ? 40 : 23}
        width={icon ? 6 : 2}
      />
    {:else if typeof icon === 'string'}
      <Icon {icon} />
    {:else}
      {#if prependIcon}
        <Icon icon={prependIcon} />
      {/if}
      <span class="button-text"><slot /></span>
      {#if appendIcon}
        <Icon icon={appendIcon} />
      {/if}
    {/if}
    {#if iconButtonHoverVariant === 'fill'}
      <div class="preload-fill">placeholder</div>
    {/if}
  </a>
{:else}
  <button
    {type}
    {formaction}
    class:icon={typeof icon === 'boolean'}
    class:icon-button={typeof icon === 'string'}
    class:loading={loading || $submitting}
    class:appendIcon
    class:prependIcon
    class:block
    class={variant}
    class:icon-hover-fill={iconButtonHoverVariant === 'fill'}
    class:rounded
    class:error={color === 'error'}
    class:success={color === 'success'}
    class:disabled
    {...$$restProps}
    on:mouseenter={handleEnter}
    on:focus={handleEnter}
    on:mouseleave={handleLeave}
    on:blur={handleLeave}
    on:click
    on:touchstart
  >
    {#if loading || ($submitting && (type === 'submit' || formaction))}
      <CircularProgress
        indeterminate
        size={icon ? 40 : 23}
        width={icon ? 6 : 2}
      />
    {:else if typeof icon === 'string'}
      <Icon {icon} />
    {:else}
      {#if prependIcon}
        <Icon icon={prependIcon} />
      {/if}
      <span class="button-text"><slot /></span>
      {#if appendIcon}
        <Icon icon={appendIcon} />
      {/if}
    {/if}
    {#if iconButtonHoverVariant === 'fill'}
      <div class="preload-fill">placeholder</div>
    {/if}
  </button>
{/if}

<style lang="scss">
  a,
  button {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    padding: 0.75em 1em;
    background: var(--button-color, var(--color-primary));
    color: var(--button-text-color, var(--color-on-primary));
    --icon-color: var(
      --button-icon-color,
      var(--button-text-color, var(--color-on-primary))
    );
    --circular-progress-color: var(
      --button-text-color,
      var(--color-on-primary)
    );
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-family: inherit;
    font-size: var(--button-font-size, 1em);
    font-weight: var(--button-font-weight, bold);
    transition:
      background-color 0.125s,
      color 0.125s,
      box-shadow 250ms;
    min-width: 0;
    max-width: max-content;
    height: var(--button-height, max-content);
    text-decoration: none;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      pointer-events: none;
      background: var(--button-text-color, var(--color-on-primary));
      opacity: 0;
      border-radius: inherit;
      transition: opacity 0.2s ease-in-out;
    }

    .button-text {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }

    &.block {
      max-width: 100%;
      width: 100%;
    }

    &.rounded {
      border-radius: calc(var(--button-font-size, 1em) * 2);
    }

    &.rounded.icon-button {
      border-radius: 50%;
    }

    &:not(.disabled):not(.flat):not(.outlined):not(.text):not(.tonal):not(
        .link
      ) {
      box-shadow:
        0 3px 1px -2px rgba(0, 0, 0, var(--box-shadow-opacity-1, 0.2)),
        0 2px 2px 0 rgba(0, 0, 0, var(--box-shadow-opacity-2, 0.14)),
        0 1px 5px 0 rgba(0, 0, 0, var(--box-shadow-opacity-3, 0.12));
    }

    &.disabled {
      cursor: default;
      pointer-events: none;
      background-color: var(--color-disabled);
      color: rgba(0, 0, 0, 0.26);
      --icon-color: rgba(0, 0, 0, 0.26);
    }

    &:not(.disabled):not(.outlined):not(.text):not(.tonal):hover:after {
      opacity: 0.08;
    }

    &.icon {
      padding: 0;
    }

    &.icon-button {
      width: var(--icon-button-size, 2.5em);
      height: var(--icon-button-size, 2.5em);
      padding: 1em;
      --icon-size: calc(1.5 / 2.5 * var(--icon-button-size, 2.5em));
      --icon-margin: 0;

      &.loading {
        padding: 0.5em;
      }

      &.icon-hover-fill {
        width: max-content;
        height: max-content;
        padding: 0;
      }
    }

    .preload-fill {
      visibility: hidden;
      font-family: 'Phosphor-Fill';
    }

    &.loading {
      pointer-events: none;
    }

    &.outlined {
      border: 1px solid var(--button-text-color, var(--color-on-surface));
      padding: calc(0.75em - 1px) 1em;

      &.disabled {
        border-color: var(--color-disabled);
      }
    }

    &.outlined,
    &.tonal,
    &.text {
      background: transparent;
      color: var(--button-text-color, var(--color-on-surface));
      --icon-color: var(
        --button-icon-color,
        var(--button-text-color, var(--color-on-surface))
      );
      --circular-progress-color: var(
        --button-text-color,
        var(--color-on-surface)
      );

      &.disabled {
        color: rgba(var(--color-on-surface-rgb), 0.26);
        --icon-color: rgba(var(--color-on-surface-rgb), 0.26);
      }

      &:not(.disabled):not(.icon-hover-fill):hover {
        background: rgba(
          var(--button-text-color-rgb, var(--color-on-surface-rgb)),
          var(--button-text-hover-opacity, 0.08)
        );
      }
    }

    &.tonal {
      background: var(
        --button-color-rgb,
        rgba(var(--color-on-surface-rgb), 0.08)
      );

      &:not(.disabled):hover {
        background: rgba(
          var(--button-text-color-rgb, var(--color-on-surface-rgb)),
          var(--button-text-hover-opacity, 0.12)
        );
      }
    }

    &.link {
      display: inline-block;
      background: transparent;
      color: var(--button-text-color, var(--color-on-surface));
      text-decoration: underline;
      font-weight: normal;
      padding: 0;
    }

    &.appendIcon:not(.prependIcon) {
      :global(i) {
        margin-right: 0;
      }
    }

    &.prependIcon:not(.appendIcon) {
      :global(i) {
        margin-left: 0;
      }
    }

    &.appendIcon.prependIcon {
      :global(i):first-of-type {
        margin-left: 0;
      }

      :global(i):last-of-type {
        margin-right: 0;
      }
    }

    &:not(.disabled).error {
      --button-color: var(--color-error);
      --button-text-color: #fff;
      --circular-progress-color: #fff;
    }

    &:not(.disabled).success {
      --button-color: var(--color-success);
      --button-text-color: #fff;
      --circular-progress-color: #fff;
    }

    &:not(.disabled).error.icon-button.text {
      --button-icon-color: var(--color-error);
    }

    &:not(.disabled).success.icon-button.text {
      --button-icon-color: var(--color-success);
    }
  }
</style>
